import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body text. Pass a node for richer content (e.g. highlighting the item name). */
  message: React.ReactNode;
  /** Optional slot rendered under the message, e.g. a preview of what is about to be sent or changed. */
  preview?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use 'error' for destructive actions (delete, revoke, remove). */
  confirmColor?: 'primary' | 'error' | 'warning';
  /** Disables both buttons while the confirmed action is in flight. */
  loading?: boolean;
  /** Disables the confirm button only, e.g. while a server preview says the action cannot run; Cancel stays live. */
  confirmDisabled?: boolean;
  /**
   * When set, the user must type this exact phrase (case-sensitive, surrounding
   * whitespace ignored) before the confirm button is enabled. Use for
   * irreversible or bulk actions.
   */
  requireText?: string;
  /** Overrides the label shown on the "type to confirm" input. */
  requireTextLabel?: string;
  /** Widen past the default `xs` when `preview` holds a list. */
  maxWidth?: 'xs' | 'sm' | 'md';
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  preview,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  loading = false,
  confirmDisabled = false,
  requireText,
  requireTextLabel,
  maxWidth = 'xs',
  onConfirm,
  onClose,
}) => {
  const [typed, setTyped] = useState('');

  // Start empty every time the dialog is (re)opened.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const phraseMatches = !requireText || typed.trim() === requireText;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? (
          <DialogContentText>{message}</DialogContentText>
        ) : (
          message
        )}
        {preview}
        {requireText && (
          <TextField
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && phraseMatches && !loading && !confirmDisabled) {
                e.preventDefault();
                onConfirm();
              }
            }}
            label={requireTextLabel ?? `Type "${requireText}" to confirm`}
            fullWidth
            size="small"
            autoFocus
            autoComplete="off"
            disabled={loading}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading || confirmDisabled || !phraseMatches}
          autoFocus={!requireText}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
